import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { t } from 'i18next';

import { BaseComponent } from '../../BaseComponent';
import { LayoutType, InitScrollWithInput } from '../../../constants/LayoutConstants';
import { ProjectManagerStoreState, ProjectManagerStore } from '../../../stores/ProjectManagerStore';
import { SortType, PersistentStore } from '../../../stores/PersistentStore';
import { UIHelper } from '../../../utils/UIHelper';
import { AppConfigurationStore } from '../../../stores/AppConfigurationStore';
import { ProjectInstance } from '../../../utils/ProjectInstance';
import { ProjectTile } from './ProjectTile';
import { ProjectHelper } from '../../../utils/ProjectHelper';
import { ProjectManagerActions } from '../../../actions/ProjectManagerActions';
import { WrapForScroll } from '../WrapForScroll';
import { ProjectManagerHeaderSmall } from './ProjectManagerHeaderSmall';
import { InputBox } from '../../InputBox';
import { ClickButton } from '../../ClickButton';
import { SceneActions } from '../../../actions/SceneActions';
import { ProjectTilesGroup } from './ProjectTilesGroup';
import { ProjectManagerHeader } from './ProjectManagerHeader';

interface ProjectManagerProps extends React.Props<ProjectManager> {
    visible: boolean;
    layoutType: LayoutType;
}

export interface ProjectManagerState extends ProjectManagerStoreState {
    sortType: SortType;
}

export class ProjectManager extends BaseComponent<ProjectManagerProps, ProjectManagerState> {
    private wasHidden: boolean = true;

    public componentWillMount(): void {
        this.addStoreListener(ProjectManagerStore, this.onChange);
        this.addStoreListener(PersistentStore, this.onChange);
        this.onChange();
    }

    public componentDidUpdate(): void {
        if (this.props.visible) {
            UIHelper.updateScrollbar(
                ReactDOM.findDOMNode(this) as Element,
                (AppConfigurationStore.getState().inApp ? '.sidebar-content' : '.scrollbar-wcf'),
                this.wasHidden ? InitScrollWithInput : null
            );
            this.wasHidden = false;
        }
    }

    public render(): JSX.Element | null {
        if (!this.props.visible) {
            this.wasHidden = true;
            return null;
        }
        switch (this.props.layoutType) {
            case LayoutType.SMALL:
                return (
                    <WrapForScroll className="sidebar-tab-container">
                        <ProjectManagerHeaderSmall
                            layoutType={this.props.layoutType}
                            editMode={this.state.editMode}
                            projectsCount={this.state.projects.length}
                            selectedProjects={this.state.selectedProjects}
                        />
                        <div className={'sidebar-content project-manager' + (UIHelper.customScrollbarEnabled() ? ' scrollbar-wcf' : '')}>
                            <InputBox
                                className="filter-input"
                                defaultValue={this.state.filter}
                                changeCallback={ProjectManagerActions.applyFilter}
                                onFocus={this.scrollToTop}
                                placeholder={t('Filter')}
                            />
                            <div className="project-content">
                                {this.state.editMode ?
                                    null :
                                    <div className="long-button-group">
                                        <ClickButton
                                            className="long-button with-icon new-project-button"
                                            callback={() => {SceneActions.newScene(); }}
                                        >
                                            {t('Create new project')}&nbsp;&hellip;
                                        </ClickButton>
                                    </div>
                                }
                                {this.state.projects.length ?
                                    (
                                        <ProjectTilesGroup
                                            projects={ProjectManagerStore.getSortedProjects(this.state.sortType)}
                                            sortType={this.state.sortType}
                                        />
                                    ) : null}
                            </div>
                        </div>
                    </WrapForScroll>
                );
            default:
                return (
                    <WrapForScroll className="sidebar-tab-container">
                        <ProjectManagerHeader
                            layoutType={LayoutType.TABLET}
                            editMode={this.state.editMode}
                            projectsCount={this.state.projects.length}
                            selectedProjects={this.state.selectedProjects}
                        />
                        <div className={'sidebar-content project-manager' + (UIHelper.customScrollbarEnabled() ? ' scrollbar-wcf' : '')}>
                            <InputBox
                                className="filter-input"
                                defaultValue={this.state.filter}
                                changeCallback={ProjectManagerActions.applyFilter}
                                onFocus={this.scrollToTop}
                                placeholder={t('Filter')}
                            />
                            <div className={'project-tiles' + (this.state.editMode ? ' projects-edit' : '')} onClick={ProjectManagerActions.removeSelection}>
                                <ProjectTile
                                    key="new-project"
                                    project={null}
                                    disabled={this.state.editMode}
                                    layoutType={this.props.layoutType}
                                    maybeUnloadable={false}
                                />
                                {ProjectManagerStore.getSortedProjects(this.state.sortType).map(this.getTile)}
                            </div>
                        </div>
                    </WrapForScroll>
                );
        }
    }

    protected onChange = (): void => {
        this.setState(jQuery.extend(ProjectManagerStore.getState(), {sortType: PersistentStore.getState().projectSort}));
    };

    protected scrollToTop = (): void => {
        UIHelper.updateScrollbar(ReactDOM.findDOMNode(this) as Element, '.scrollbar-wcf', 0);
    };

    protected getTile = (projectItem: ProjectInstance, index: number, items: Array<ProjectInstance>): React.ReactNode => {
        return (
            <ProjectTile
                key={projectItem.id}
                project={projectItem}
                disabled={this.state.editMode && projectItem.name === 'new'}
                layoutType={this.props.layoutType}
                last={index === items.length - 1}
                maybeUnloadable={!ProjectHelper.canSimplyLoadProject(projectItem.attributes)}
            />
        );
    };

    protected toggleEdit = () => {
        if (this.state.editMode) {
            ProjectManagerActions.disableEditMode();
        } else {
            ProjectManagerActions.enableEditMode();
        }
    };
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/sidebar/projectmanager/ProjectManager.tsx